import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Button, 
  Form, 
  Spinner, 
  Row, 
  Col,
  Card,
  Alert,
  Badge,
  InputGroup
} from 'react-bootstrap';
import { 
  upsertFacture, 
  FactureInfo, 
  getFactures 
} from '../services/EditFacture_service';
import { toast } from 'react-toastify';
import Switch from "react-switch";  
import axios from 'axios';
import { 
  isValidPostalCode 
} from '../utils/functions/validatePostalCode';
import { 
  isValidRcs 
} from '../utils/functions/validateRCSNumber';
import { 
  fetchPostalCodeDetails 
} from '../utils/functions/fetchPostalCodeDetails';
import { 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaCity, 
  FaHashtag, 
  FaEuroSign,
  FaCheckCircle,
  FaExclamationTriangle
} from 'react-icons/fa';

interface EditFactureModalProps {
  show: boolean; 
  onClose: () => void;
}

const EditFactureModal: React.FC<EditFactureModalProps> = ({ show, onClose }) => {
  const [formData, setFormData] = useState<FactureInfo>({
    id: undefined,
    raison_sociale: '',
    adresse: '',
    code_postal: '',
    ville: '',
    rcs: '',
    tv_intra: '',
    active: false
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cities, setCities] = useState<string[]>([]);
  const [rcsValid, setRcsValid] = useState<boolean | null>(null);
  const [postalCodeValid, setPostalCodeValid] = useState<boolean | null>(null);

  useEffect(() => {
    if (show) {
      setLoading(true);
      getFactures()
        .then(data => {
          if (data) {
            setFormData(data);
            if (data.code_postal && isValidPostalCode(data.code_postal)) {
              setPostalCodeValid(true);
            }
            if (data.rcs && isValidRcs(data.rcs)) {
              setRcsValid(true);
            }
          }
        })
        .catch(err => {
          console.error(err);
          toast.error('Erreur lors du chargement des données');
        })
        .finally(() => setLoading(false));
    } else {
      // Réinitialiser à la fermeture
      setRcsValid(null);
      setPostalCodeValid(null);
      setCities([]);
    }
  }, [show]);

  const handlePostalCodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const code_postal = e.target.value;
    
    // Validation : uniquement des chiffres, max 5
    if (!/^\d{0,5}$/.test(code_postal)) return;

    setFormData(prev => ({ ...prev, code_postal }));

    if (isValidPostalCode(code_postal)) {
      setPostalCodeValid(true);
      try {
        const details = await fetchPostalCodeDetails(code_postal);
        const citiesList = details?.cities || [];
        setCities(citiesList);
        if (citiesList.length === 1) {
          setFormData(prev => ({ ...prev, ville: citiesList[0] }));
        }
      } catch (err) {
        console.error("Erreur de code postal :", err);
        setCities([]);
        setPostalCodeValid(false);
      }
    } else {
      setPostalCodeValid(code_postal.length === 0 ? null : false);
      setCities([]);
    }
  };

  const handleRcsChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rcs = e.target.value;
    
    // Validation : uniquement des chiffres, max 9
    if (!/^\d{0,9}$/.test(rcs)) return;

    setFormData(prev => ({ ...prev, rcs }));

    if (isValidRcs(rcs)) {
      setRcsValid(true);
      try {
        setLoading(true);
        const [response, nafResponse] = await Promise.all([
          axios.get(`/rne/${rcs}`),
          axios.get(`/rne/naf/${rcs}`)
        ]);

        if (response.data?.data) {
          const company = response.data.data;
          const postalCode = company.postalCode;
          let cityList: string[] = [];

          if (postalCode && isValidPostalCode(postalCode)) {
            const details = await fetchPostalCodeDetails(postalCode);
            cityList = details?.cities || [];
            setCities(cityList);
          }

          setFormData(prev => ({
            ...prev,
            raison_sociale: company.companyName || prev.raison_sociale,
            adresse: company.address || prev.adresse,
            code_postal: postalCode || prev.code_postal,
            ville: cityList[0] || company.cities?.[0] || prev.ville,
            tv_intra: nafResponse.data?.data?.vat_number || prev.tv_intra,
          }));
          
          if (postalCode) setPostalCodeValid(true);
        }
      } catch (err) {
        console.error("Erreur RCS :", err);
        setRcsValid(false);
        toast.warning('RCS non trouvé ou erreur de connexion');
      } finally {
        setLoading(false);
      }
    } else {
      setRcsValid(rcs.length === 0 ? null : false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.raison_sociale?.trim()) {
      toast.error('La raison sociale est obligatoire');
      return;
    }

    setSaving(true);
    try {
      await upsertFacture(formData);
      toast.success(
        <div>
          <FaCheckCircle className="me-2" />
          Informations de facturation mises à jour avec succès
        </div>
      );
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(
        <div>
          <FaExclamationTriangle className="me-2" />
          Erreur lors de la mise à jour
        </div>
      );
    } finally {
      setSaving(false);
    }
  };

  const getInputValidation = (valid: boolean | null) => {
    if (valid === null) return {};
    return {
      isValid: valid,
      isInvalid: !valid
    };
  };

  return (
    <Modal 
      show={show} 
      onHide={onClose} 
      centered 
      size="lg" 
      backdrop="static"
      className="edit-facture-modal"
    >
      <Modal.Header 
        closeButton 
        className="bg-gradient-primary text-white"
        style={{
          background: 'linear-gradient(135deg, #4BB0C0 0%, #2a8d9c 100%)',
          borderBottom: 'none'
        }}
      >
        <Modal.Title className="d-flex align-items-center">
          <FaBuilding className="me-3" size={24} />
          <div>
            <h5 className="mb-0">Informations de facturation</h5>
            <small className="opacity-75">
              {formData.active ? 'En-tête activée' : 'En-tête désactivée'}
            </small>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Chargement des informations...</p>
          </div>
        ) : (
          <>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    <FaHashtag className="me-2" />
                    En-tête de facture
                  </h6>
                  <Badge 
                    bg={formData.active ? "success" : "secondary"}
                    className="px-3 py-2"
                  >
                    {formData.active ? "Activée" : "Désactivée"}
                  </Badge>
                </div>
                <p className="text-muted mb-0 small">
                  Activez ou désactivez l'affichage de l'en-tête sur vos factures
                </p>
                <div className="mt-3 d-flex align-items-center">
                  <Switch
                    onChange={(checked) => setFormData({ ...formData, active: checked })}
                    checked={formData.active || false}
                    onColor="#4BB0C0"
                    offColor="#dee2e6"
                    onHandleColor="#ffffff"
                    offHandleColor="#ffffff"
                    uncheckedIcon={false}
                    checkedIcon={false}
                    height={24}
                    width={48}
                    className="me-3"
                  />
                  <span className={formData.active ? "text-success fw-bold" : "text-muted"}>
                    {formData.active 
                      ? "✓ L'en-tête sera visible sur les factures" 
                      : "✗ L'en-tête sera masquée sur les factures"}
                  </span>
                </div>
              </Card.Body>
            </Card>

            <Alert variant="info" className="d-flex align-items-center">
              <FaExclamationTriangle className="me-2" />
              <small>
                Renseignez votre numéro RCS pour pré-remplir automatiquement les informations
              </small>
            </Alert>

            <Row className="g-4">
              {/* RCS / SIREN */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold d-flex align-items-center">
                    <FaHashtag className="me-2" />
                    Numéro RCS / SIREN
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light">
                      FR
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="rcs"
                      value={formData.rcs}
                      onChange={handleRcsChange}
                      placeholder="123456789"
                      maxLength={9}
                      {...getInputValidation(rcsValid)}
                    />
                    {rcsValid === true && (
                      <InputGroup.Text className="bg-success text-white">
                        <FaCheckCircle />
                      </InputGroup.Text>
                    )}
                    {rcsValid === false && (
                      <InputGroup.Text className="bg-danger text-white">
                        <FaExclamationTriangle />
                      </InputGroup.Text>
                    )}
                  </InputGroup>
                  <Form.Text className={rcsValid === false ? "text-danger" : "text-muted"}>
                    {rcsValid === false 
                      ? "Numéro RCS invalide ou non trouvé" 
                      : "9 chiffres requis (SIREN)"}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Raison sociale */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold d-flex align-items-center">
                    <FaBuilding className="me-2" />
                    Raison sociale *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="raison_sociale"
                    value={formData.raison_sociale}
                    onChange={handleChange}
                    placeholder="Nom de votre entreprise"
                    required
                  />
                </Form.Group>
              </Col>

              {/* Adresse */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold d-flex align-items-center">
                    <FaMapMarkerAlt className="me-2" />
                    Adresse
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    placeholder="Adresse postale"
                  />
                </Form.Group>
              </Col>

              {/* Code postal */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold d-flex align-items-center">
                    <FaHashtag className="me-2" />
                    Code postal
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      name="code_postal"
                      value={formData.code_postal}
                      onChange={handlePostalCodeChange}
                      placeholder="75000"
                      maxLength={5}
                      {...getInputValidation(postalCodeValid)}
                    />
                    {postalCodeValid === true && (
                      <InputGroup.Text className="bg-success text-white">
                        <FaCheckCircle />
                      </InputGroup.Text>
                    )}
                    {postalCodeValid === false && (
                      <InputGroup.Text className="bg-danger text-white">
                        <FaExclamationTriangle />
                      </InputGroup.Text>
                    )}
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Le code postal est automatiquement validé
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Ville */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold d-flex align-items-center">
                    <FaCity className="me-2" />
                    Ville
                  </Form.Label>
                  {cities.length > 1 ? (
                    <Form.Select
                      name="ville"
                      value={formData.ville}
                      onChange={handleChange}
                      className="form-select-lg"
                    >
                      <option value="">Sélectionner une ville</option>
                      {cities.map((c, i) => (
                        <option key={i} value={c}>{c}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      name="ville"
                      value={formData.ville}
                      onChange={handleChange}
                      placeholder="Ville"
                    />
                  )}
                  {cities.length > 1 && (
                    <Form.Text className="text-muted">
                      {cities.length} villes disponibles pour ce code postal
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              {/* TVA intracommunautaire */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold d-flex align-items-center">
                    <FaEuroSign className="me-2" />
                    TVA intracommunautaire
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="tv_intra"
                    value={formData.tv_intra}
                    onChange={handleChange}
                    placeholder="FRXXXXXXXXXXXXX"
                  />
                  <Form.Text className="text-muted">
                    Format : FR + 11 chiffres (ex: FR12345678901)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Card className="border-info mt-4 bg-info bg-opacity-10">
              <Card.Body>
                <h6 className="text-info mb-2">
                  <FaCheckCircle className="me-2" />
                  Validation automatique
                </h6>
                <p className="mb-0 small text-muted">
                  Le système valide automatiquement le RCS et le code postal. 
                  Les champs marqués d'une astérisque (*) sont obligatoires.
                </p>
              </Card.Body>
            </Card>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0">
        <Button 
          variant="outline-secondary" 
          onClick={onClose}
          className="px-4"
          disabled={saving}
        >
          Annuler
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={saving || !formData.raison_sociale?.trim()}
          className="px-4"
          style={{
            background: 'linear-gradient(135deg, #4BB0C0 0%, #2a8d9c 100%)',
            border: 'none'
          }}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Enregistrement...
            </>
          ) : (
            'Enregistrer les modifications'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditFactureModal;